import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * Quantify
 *--------------------------------------------------------------------------
 *
 * This extension allows for the use of buttons to increase/decrease item
 * quantities on the product and basket pages. When used on the basket page,
 * the decrease button becomes a remove button if the quantity is 1.
 *
 */
export default class Quantify {

	/**
	 * Parameter Validator object
	 *
	 * @var	@mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Create a new Quantify object
	 *
	 * @param	ElementList	decrementElems	Decrement Elements. Defaults to '[data-action="decrement"]'.
	 * @param	Element		formElement		Form Element. If you want the form to submit on Quantity changed, pass this through. Not required. Defaults to null.
	 * @param	ElementList	incrementElems	Increment Elements. Defaults to '[data-action="increment"]'.
	 * @param	Element		quantityElem	Quantity Element. Defaults to '[name="Quantity"]'.
	 * @param	Element		wrapper			Top-level element for quantify fields. Defaults to '[data-hook="quantify"]'.
	 *
	 */
	constructor ({
		decrementElems = '[data-action="decrement"]',
		formElement = null,
		incrementElems = '[data-action="increment"]',
		quantityElem = '[name="Quantity"]',
		wrapper = '[data-hook="quantify"]'
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.wrapper = this.#validator.isElement('wrapper', wrapper);

		this.decrementElems = this.#validator.isElementList('decrementElems', decrementElems, {container: this.wrapper});
		this.incrementElems = this.#validator.isElementList('incrementElems', incrementElems, {container: this.wrapper});
		this.quantityElem = this.#validator.isElement('quantityElem', quantityElem, {container: this.wrapper});
		this.formElement = this.#validator.isElement('formElement', formElement, {required: false});

		this.maxQuantity = parseInt(this.quantityElem.getAttribute('max'), 10);
		this.minQuantity = parseInt(this.quantityElem.getAttribute('min'), 10);

		if (isNaN(this.maxQuantity)) {
			this.maxQuantity = 0;
		}

		if (isNaN(this.minQuantity)) {
			this.minQuantity = 0;
		}

		this.bindEvents();
		this.elemsUpdate();
	}

	bindEvents () {

		this.quantityElem.addEventListener('change', () => this.quantityChanged());

		for (const decrementElem of this.decrementElems) {
			decrementElem.addEventListener('click', (e) => this.decrement(e));
		}

		for (const incrementElem of this.incrementElems) {
			incrementElem.addEventListener('click', (e) => this.increment(e));
		}

	}

	elemsUpdate () {
		let quantity = parseInt(this.quantityElem.value, 10);

		if (quantity <= this.minQuantity) {
			this.quantityElem.value = this.minQuantity;
		}

		if (quantity >= this.maxQuantity && this.maxQuantity > 0) {
			this.quantityElem.value = this.maxQuantity;
		}

		this.disableDecrements(quantity <= this.minQuantity);
		this.disableIncrements(quantity >= this.maxQuantity && this.maxQuantity > 0);
	}

	quantityChanged () {
		this.elemsUpdate();

		// If Form Element is passed through, submit the form (Example: Submit on Basket Page)
		if (this.formElement) {
			this.formElement.submit();
		}
	}

	decrement (e) {
		e.preventDefault();

		let quantity = parseInt(this.quantityElem.value, 10);

		if (quantity <= this.minQuantity) {
			this.quantityElem.value = this.minQuantity;
		} else {
			this.quantityElem.value = quantity - 1;
		}

		this.quantityElem.dispatchEvent(new Event('change', {bubbles: true}));
	}

	increment (e) {
		e.preventDefault();

		let quantity = parseInt(this.quantityElem.value, 10);

		if (quantity >= this.maxQuantity && this.maxQuantity > 0) {
			this.quantityElem.value = this.maxQuantity;
		} else {
			this.quantityElem.value = quantity + 1;
		}

		this.quantityElem.dispatchEvent(new Event('change', {bubbles: true}));
	}

	disableDecrements (disabled) {
		for (const decrementElem of this.decrementElems) {
			decrementElem.disabled = disabled;
		}
	}

	disableIncrements (disabled) {
		for (const incrementElem of this.incrementElems) {
			incrementElem.disabled = disabled;
		}
	}

}
